import { Button, Checkbox, Collapse } from 'antd';
import EventJourney from 'emp-3.0/components/Sankey/Configurations/EventJourney';
import { cloneDeep, find, findIndex, flatten, isEmpty, orderBy } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Icon, { CaretRightOutlined } from '@ant-design/icons';
import { partitionOptions } from '@components/dimensions/utils/partition';
import { useQueryDimensionData } from '@components/mapping-panel/api';
import PlusIcon from '@icons/emp/plus_icon.svg';
import { ConvivaEvent } from '@src/common/constant/enum';
import { useGetSankeyDimensions } from '@src/emp-3.0/api/queryHooks';
import AutoBreakdown from '@src/emp-3.0/components/Sankey/Configurations/AutoBreakdown';
import FilterByEvents from '@src/emp-3.0/components/Sankey/Configurations/FilterByEvents';
import { IFilterByEvents } from '@src/emp-3.0/components/Sankey/Configurations/FilterByEvents/typed';
import LandingEvent from '@src/emp-3.0/components/Sankey/Configurations/LandingEvent';
import InitDialog from '@src/emp-3.0/components/Sankey/InitDialog';
import { dimensionValueLabelRender } from '@src/emp-3.0/components/Sankey/components/CompactDualSelect';
import { useSankeyStore } from '@src/emp-3.0/components/Sankey/store';
import {
  generateAutoBreakdownEventFilters,
  generateLandingEventFilters,
  isSubsetFilters,
  isValidFilter,
  mergeEventsFilterArrays,
  trimEventFilter,
} from '@src/emp-3.0/components/Sankey/utils';
import { IEventFilter } from '@src/emp-3.0/types';
import { APP_TOTAL_USER_EVENTS } from '@src/emp-3.0/utils/constants';
import { parseDataToOptions } from '@src/emp-3.0/utils/parse';

import { LANDING_EVENTS_WITH_MAX_DIMENSION_RULES_COUNT } from '../constants';
import { CollapseHeader, EventJourneySelect, StyledCollapse, Title } from './styled';
import styles from './styles.less';
import { getDefaultBreakdownEvent } from '../SankeyChart/utils';

const EMPTY_ARRAY = [];
const EVENT_NAME = 'event_name';
export const eventJourneyOptions = [
  {
    label: 'Starting with',
    value: 'forward',
  },
  {
    label: 'Ending with',
    value: 'backward',
  },
];

const findMatchingEvent = (eventBreakdown, eventName) => {
  return find(eventBreakdown, { event_name: eventName });
};

export const RenderEventJourney = ({
  isCollapse,
  landing_events,
  direction,
  interval,
  filters,
  isEventNameLoading,
  eventNameOptions,
  eventDimensionOptions,
  emitValues,
}: {
  isCollapse: boolean;
  landing_events: any;
  direction: any;
  isEventNameLoading: boolean;
  eventNameOptions: any;
  eventDimensionOptions: any;
  interval?: string;
  filters?: IEventFilter[][];
  emitValues?: any;
}) => {
  const [landingEvents, setLandingEvents] = useState(landing_events);
  const [directionCurrentValue, setDirectionCurrentValue] = useState(direction);
  const { setSankeyRequestBody } = useSankeyStore((state) => state);

  useEffect(() => {
    setLandingEvents(landing_events);
  }, [landing_events]);

  useEffect(() => {
    setDirectionCurrentValue(direction);
  }, [direction]);

  const addDimension = () => {
    const cloneLandingEvents = cloneDeep(landingEvents);
    cloneLandingEvents[0].filters.push([
      {
        field: '',
        op: 'eq',
        value: [],
        is_all: false,
      },
    ]);
    setLandingEvents(cloneLandingEvents);
  };
  const eventsChange = (index, eventData) => {
    const cloneLandingEvents = cloneDeep(landingEvents);
    cloneLandingEvents[0] = eventData;
    setLandingEvents(cloneLandingEvents);
    emitValues({ index, eventData, direction: directionCurrentValue });
  };

  const EventJourneyContent = (
    <div style={{ paddingLeft: isCollapse ? 24 : 0 }}>
      {landingEvents.map((event, index) => {
        const showAddDimensionButton =
          Array.isArray(event?.filters) &&
          event.filters.length < LANDING_EVENTS_WITH_MAX_DIMENSION_RULES_COUNT;
        let addDimensionButtonText = 'Add Dimension';
        if (event.filters.length !== 0) {
          addDimensionButtonText += `(${event.filters.length})`;
        }

        return (
          <EventJourney
            key={`${event.event_name}_${index}`}
            loading={isEventNameLoading}
            interval={interval}
            filters={filters}
            showDelete={false}
            addDimensionButtonProps={{
              show: showAddDimensionButton,
              text: addDimensionButtonText,
            }}
            eventData={event}
            eventOptions={eventNameOptions}
            onAddDimension={addDimension}
            onChange={(...rest) => eventsChange(index, ...rest)}
            onEventChange={(...rest) => eventsChange(index, ...rest)}
            compactDualSelectProps={{
              dimensionSelectProps: {
                options: eventDimensionOptions,
              },
              dimensionValueSelectProps: {
                mode: undefined,
              },
            }}
          />
        );
      })}
    </div>
  );

  if (isCollapse) {
    return (
      <Collapse.Panel
        key={1}
        header={<CollapseHeader>View Paths</CollapseHeader>}
        extra={
          <EventJourneySelect
            options={eventJourneyOptions}
            defaultValue={eventJourneyOptions[0].value}
            value={directionCurrentValue}
            bordered={false}
            onClick={(e) => e.stopPropagation()}
            onChange={(value) => {
              setSankeyRequestBody({ direction: value });
            }}
          />
        }
      >
        {EventJourneyContent}
      </Collapse.Panel>
    );
  }

  return (
    <div>
      <div>
        <CollapseHeader>
          <span style={{ display: 'inline-block', marginRight: '10px' }}>View Paths </span>
          <EventJourneySelect
            options={eventJourneyOptions}
            defaultValue={eventJourneyOptions[0].value}
            value={directionCurrentValue}
            bordered={false}
            onClick={(e) => e.stopPropagation()}
            onChange={(value) => {
              setDirectionCurrentValue(value);
            }}
          />
        </CollapseHeader>
      </div>
      {EventJourneyContent}
    </div>
  );
};

const Configurations = ({
  onFetch,
  onModalClose,
  sankeyLoading,
  initSankeyRequestBody,
  style,
  eventNameFromUrl,
  calenderBoundry,
}) => {
  const { sankeyRequestBody, setSankeyRequestBody, showConfigModal, checkedOnly, setCheckedOnly } =
    useSankeyStore((state) => state);
  const {
    filters,
    landing_events,
    direction,
    interval,
    event_breakdown,
    event_name_filters,
    network_request_enabled,
  } = sankeyRequestBody;

  const [eventList, setEventList] = useState<IFilterByEvents['data']>([]);
  const [hightlightBreakdownSection, setHightlightBreakdownSection] = useState(false);
  const breakdownSectionRef = useRef();
  const navigate = useNavigate();

  const { data: sankeyDimensionList = EMPTY_ARRAY } = useGetSankeyDimensions();
  const { data: eventNameData = EMPTY_ARRAY, isFetching: isEventNameLoading } =
    useQueryDimensionData(
      {
        fields: [EVENT_NAME],
        interval: interval,
        filters: filters,
        params: {
          src: 'trends',
        },
      },
      500,
    );

  const handleNodeBreakdownClick = () => {
    const { current }: any = breakdownSectionRef;
    if (current) {
      current.scrollIntoView({ behavior: 'smooth' });
    }
    setHightlightBreakdownSection(true);
    setTimeout(() => {
      setHightlightBreakdownSection(false);
    }, 5000);
  };

  useEffect(() => {
    document.addEventListener('SANKEY_BREAKDOWN_EVENT', handleNodeBreakdownClick);
    return () => document.removeEventListener('SANKEY_BREAKDOWN_EVENT', handleNodeBreakdownClick);
  }, []);

  const { defaultOptions: eventDimensionOptions } = partitionOptions(
    sankeyDimensionList.filter((item) => item.can_breakdown === 1),
  );
  const { defaultOptions: dimensionOptions } = partitionOptions(
    sankeyDimensionList.filter((item) => item.can_filter === 1),
    (dimension) => filters.some((item) => item[0].field === dimension.dimension_name),
  );

  const breakdownEventNameOptions = parseDataToOptions(
    eventNameData.filter((item) => event_name_filters.includes(item[EVENT_NAME])),
    {
      label: EVENT_NAME,
      value: EVENT_NAME,
      disabledCallback: (item) =>
        event_breakdown.some((event) => item[EVENT_NAME] === event[EVENT_NAME]),
      labelRender: dimensionValueLabelRender,
    },
  );

  const matchedEventBreakdown = useMemo(
    () => findMatchingEvent(event_breakdown, landing_events[0].event_name),
    [landing_events, event_breakdown],
  );

  useEffect(() => {
    // if no event list is returned from api for any old interval, then take the selected events from payload for list calculation
    const eventNames = isEmpty(eventNameData)
      ? event_name_filters.map((event) => {
          return {
            [EVENT_NAME]: event,
            [APP_TOTAL_USER_EVENTS]: 0,
          } as any;
        })
      : eventNameData;

    const eventList = eventNames
      .filter((item) =>
        network_request_enabled === 0
          ? item[EVENT_NAME] !== ConvivaEvent.conviva_network_request
          : true,
      )
      .map((event) => {
        const name = event[EVENT_NAME];
        const count = event[APP_TOTAL_USER_EVENTS];
        return {
          name,
          count,
          checked: event_name_filters.includes(name),
          disabled:
            landing_events?.[0].event_name === name ||
            name === ConvivaEvent.conviva_network_request,
        };
      });

    const orderEventList = orderBy(eventList, ['disabled'], ['desc']);
    setEventList(orderEventList);
  }, [eventNameData, event_name_filters, landing_events, network_request_enabled]);

  useEffect(() => {
    if (eventNameFromUrl && eventList?.length) {
      const isValidEventName =
        eventNameFromUrl &&
        eventList.length &&
        eventList.some((event) => event.name === eventNameFromUrl);

      if (!isValidEventName) {
        navigate(`/paths`);
      }
    }
  }, [eventNameFromUrl, eventList]);

  const handleAddDimension = (events, index) => {
    events[index].breakdowns.push([
      {
        field: '',
        op: 'eq',
        value: [],
        is_all: false,
      },
    ]);
    setSankeyRequestBody({
      event_breakdown: events,
    });
  };

  const handleAddEvent = (e) => {
    e.stopPropagation();
    const cloneEventBreakdown = cloneDeep(event_breakdown);
    cloneEventBreakdown.push({ event_name: '', breakdowns: [] });
    setSankeyRequestBody({
      event_breakdown: cloneEventBreakdown,
    });
  };

  const handleEventsChange = (index, eventData) => {
    const eventBreakdown = event_breakdown;
    const dimensionList = sankeyDimensionList.filter((item) => item.can_breakdown === 1);
    const defaultEventBreakdown = getDefaultBreakdownEvent(eventData, dimensionList);
    eventBreakdown[index] = defaultEventBreakdown;
    setSankeyRequestBody({
      event_breakdown: eventBreakdown,
    });
  };

  const handleDimensionChange = (index, eventData) => {
    const eventBreakdown = event_breakdown;
    eventBreakdown[index] = eventData;
    setSankeyRequestBody({
      event_breakdown: eventBreakdown,
    });
  };

  const handleCheckSelectedOnly = (e) => {
    e.stopPropagation();
    const checked = e.target.checked;
    setCheckedOnly(checked);
  };

  const handleFilterByEventsChange = (data, selectedEvents) => {
    setSankeyRequestBody({
      event_name_filters: selectedEvents,
    });
  };

  const handleSwitchClick = (checked) => {
    const enabled = checked ? 1 : 0;
    setSankeyRequestBody({
      network_request_enabled: enabled,
    });
  };

  const handleDeleteEventClick = (removeIndex) => {
    const filterEventBreakdown = event_breakdown.filter((value, index) => index !== removeIndex);
    setSankeyRequestBody({
      event_breakdown: filterEventBreakdown,
    });
  };

  const onLandingEventNameChange = (eventName) => {
    const foundSameEventBreakdown = event_breakdown.find((item) => item.event_name === eventName);
    let updatedFilters: IEventFilter[][] = [];
    if (foundSameEventBreakdown) {
      updatedFilters = generateLandingEventFilters(event_breakdown, eventName);
    } else {
      event_breakdown.unshift({ event_name: eventName, breakdowns: [] });
    }
    setSankeyRequestBody({
      landing_events: [{ event_name: eventName, filters: updatedFilters }],
      event_breakdown,
    });
  };

  const onLandingEventDimensionChange = (dimensions) => {
    const landingEvent = landing_events[0];
    const landingEventName = landingEvent.event_name;

    let eventBreakdown = cloneDeep(event_breakdown);
    // breakdown logic
    if (dimensions.length === 0) {
      eventBreakdown = eventBreakdown.map((event) => {
        if (event.event_name === landingEventName) {
          event.breakdowns = [];
        }
        return event;
      });
    } else {
      // sync to break down
      const copiedDimensions = cloneDeep(dimensions);
      const trimmedDimensions = trimEventFilter(copiedDimensions);
      const isValidDimension = isValidFilter(trimmedDimensions);
      const landingEventInBreakdown = eventBreakdown.find((b) => b.event_name === landingEventName);
      const updatedBreakdowns = generateAutoBreakdownEventFilters(
        trimmedDimensions,
        landingEventInBreakdown ? landingEventInBreakdown.breakdowns : [],
      );

      if (landingEventInBreakdown) {
        const breakdownsWithValues = isValidFilter(landingEventInBreakdown.breakdowns);

        if (isValidDimension) {
          landingEventInBreakdown.breakdowns = updatedBreakdowns;
        } else if (!isValidDimension && breakdownsWithValues) {
          landingEventInBreakdown.breakdowns = [];
        }
      } else if (!landingEventInBreakdown && isValidDimension) {
        eventBreakdown.unshift({
          event_name: landingEventName,
          breakdowns: updatedBreakdowns,
        });
      }
    }
    setSankeyRequestBody({
      landing_events: [{ event_name: landingEventName, filters: [...dimensions] }],
      event_breakdown: [...eventBreakdown],
    });
  };

  const handleAutoBreakdownChange = (value) => {
    const copiedEventBreakdown = cloneDeep(event_breakdown);
    const index = findIndex(copiedEventBreakdown, { event_name: value.event_name });
    copiedEventBreakdown[index] = value;
    setSankeyRequestBody({
      event_breakdown: [...copiedEventBreakdown],
    });
  };

  return (
    <div
      style={{
        width: 450,
        borderRight: '1px solid rgba(0, 0, 0, 0.15)',
        overflowX: 'scroll',
        ...style,
      }}
    >
      <Title>
        <span style={{ padding: '4px 8px' }}>Configurations</span>
        <Button
          type="primary"
          onClick={() => {
            onFetch();
          }}
          loading={sankeyLoading}
          disabled={sankeyLoading}
        >
          Refresh Paths
          <CaretRightOutlined />
        </Button>
      </Title>
      <StyledCollapse defaultActiveKey={['1', '2', '3']} ghost>
        <Collapse.Panel
          key={1}
          header={<CollapseHeader>View Paths</CollapseHeader>}
          extra={
            <EventJourneySelect
              options={eventJourneyOptions}
              defaultValue={eventJourneyOptions[0].value}
              value={direction}
              bordered={false}
              onClick={(e) => e.stopPropagation()}
              onChange={(value) => {
                setSankeyRequestBody({ direction: value });
              }}
            />
          }
        >
          <div style={{ paddingLeft: 24 }}>
            <LandingEvent
              onEventNameChange={onLandingEventNameChange}
              onDimensionChange={onLandingEventDimensionChange}
            />
            <AutoBreakdown
              data={matchedEventBreakdown}
              filters={filters}
              interval={interval}
              dimensionNameOptions={eventDimensionOptions}
              fixedBreakdowns={landing_events[0].filters}
              onDimensionChange={handleAutoBreakdownChange}
            />
          </div>
        </Collapse.Panel>

        <Collapse.Panel
          key={2}
          header={<CollapseHeader>Filter By Events</CollapseHeader>}
          extra={
            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox checked={checkedOnly} onChange={handleCheckSelectedOnly}>
                Selected only
              </Checkbox>
            </div>
          }
        >
          <FilterByEvents
            data={eventList}
            onChange={handleFilterByEventsChange}
            onSwitchClick={handleSwitchClick}
            onFetch={onFetch}
            checkedOnly={checkedOnly}
          />
        </Collapse.Panel>
        <Collapse.Panel
          ref={breakdownSectionRef}
          key={3}
          header={<CollapseHeader>Breakdown Event</CollapseHeader>}
          extra={
            <Button type="link" onClick={handleAddEvent}>
              <Icon component={PlusIcon} />
              Add Event
            </Button>
          }
        >
          <div
            className={hightlightBreakdownSection ? styles.breakdownEvent : ''}
            style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: 24 }}
          >
            {event_breakdown.map((event, index, array) => {
              const lastIndex = array.length - 1;
              const showPartingLine = index !== lastIndex;

              const landingEventName = landing_events[0].event_name;
              const isSameEvent = landingEventName === event.event_name;
              const maxDimensionApplied =
                event?.breakdowns?.length >= LANDING_EVENTS_WITH_MAX_DIMENSION_RULES_COUNT;

              if (isSameEvent) return;

              return (
                <div key={`${event.event_name}_${index}`}>
                  <EventJourney
                    loading={isEventNameLoading}
                    interval={interval}
                    filters={filters}
                    showDimensionTitle
                    eventData={event}
                    eventOptions={breakdownEventNameOptions}
                    onAddDimension={() => handleAddDimension(event_breakdown, index)}
                    onDeleteEvent={() => handleDeleteEventClick(index)}
                    onChange={(...rest) => handleDimensionChange(index, ...rest)}
                    onEventChange={(...rest) => handleEventsChange(index, ...rest)}
                    addDimensionButtonProps={{
                      show: !maxDimensionApplied,
                    }}
                    compactDualSelectProps={{
                      dimensionSelectProps: {
                        options: eventDimensionOptions,
                      },
                      dimensionValueSelectProps: {
                        mode: 'multiple',
                      },
                    }}
                  />
                  {showPartingLine && (
                    <div
                      style={{
                        height: 16,
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: '8px',
                      }}
                    >
                      <div style={{ width: '100%', height: 1, background: '#f0f0f0' }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Collapse.Panel>
      </StyledCollapse>
      <InitDialog
        initSankeyRequestBody={initSankeyRequestBody}
        dimensionOptions={dimensionOptions}
        fixedDimensions={['app_id']}
        modalProps={{ open: showConfigModal }}
        onClose={() => {
          onModalClose?.();
        }}
        // interval={interval} // not being used can be deleted later
        eventDimensionOptions={eventDimensionOptions}
        onApply={(payloadPartial) => {
          onFetch(payloadPartial);
        }}
        calenderBoundry={calenderBoundry}
      />
    </div>
  );
};

export default Configurations;
